<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier, vue/max-attributes-per-line, vue/html-closing-bracket-newline */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TalkviaStoreAPI from 'dashboard/api/talkviaStore';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  volumeDiscountFor,
  volumeTiersFor,
  volumeTotal,
} from './creditVolumePricing';

const copy = {
  title: 'Marketplace Talkvia',
  subtitle:
    'Compre créditos e adicionais e atualize a recorrência da assinatura pela Asaas.',
  loading: 'Carregando marketplace...',
  actions: {
    add: 'Adicionar',
    remove: 'Remover',
    confirm: 'Confirmar alteração',
    cancel: 'Cancelar',
    reload: 'Atualizar',
  },
  sections: {
    catalog: 'Adicionais disponíveis',
    catalogDescription:
      'Escolha os adicionais que entram na recorrência mensal.',
    cart: 'Resumo da compra',
    billing: 'Assinatura e bloqueios',
    guidance: 'Como funciona',
  },
  fields: {
    currentSubscription: 'Assinatura atual',
    openBills: 'Boletos em aberto',
    monthlyIncrease: 'Acréscimo mensal',
    newEstimatedValue: 'Novo valor estimado',
    input: 'Entrada',
    output: 'Saída',
    item: 'Item',
    quantity: 'Quantidade',
    total: 'Total',
  },
  billing: {
    notReady: 'Cobrança não vinculada',
  },
  empty: {
    cart: 'Nenhum adicional selecionado.',
    openBills: 'Nenhum boleto em aberto.',
  },
  messages: {
    blocked:
      'Existe cobrança pendente ou vencida. Regularize antes de comprar adicionais.',
    pendingPurchase: 'Existe uma compra aguardando confirmação de pagamento.',
    billingNotReady:
      'Vincule a assinatura Asaas antes de confirmar compras no marketplace.',
    success:
      'Alteração registrada. Os adicionais serão liberados após confirmação do pagamento.',
    failed: 'Não foi possível concluir a compra.',
    loadFailed: 'Não foi possível carregar o marketplace.',
  },
  labels: {
    brand: 'Talkvia',
    dash: '-',
    plus: '+',
    recommended: 'Recomendado para começar',
    recurring: 'recorrente',
    byMonth: '/mês',
    selected: 'Selecionado',
    times: 'x',
    colon: ':',
    periodMark: '.',
    included: 'Incluso',
    notIncluded: 'Não incluso',
  },
};

const storePayload = ref({});
const quantities = reactive({});
const confirmDialogRef = ref(null);
const creditExampleDialogRef = ref(null);
const selectedCreditExample = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const buyingCreditProductId = ref(null);
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const activeMarketplaceSection = ref(
  ['services', 'addons', 'corban'].includes(route.query.section)
    ? route.query.section
    : 'services'
);
const creditUnits = reactive({});

const corbanFeatures = [
  FEATURE_FLAGS.TALKVIA_CORBAN,
  FEATURE_FLAGS.TALKVIA_CORBAN_FACTA_OFFLINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_CLT_FGTS_QUERY,
  FEATURE_FLAGS.TALKVIA_CORBAN_BENEFIT360,
  FEATURE_FLAGS.TALKVIA_CORBAN_CREDIT_ENGINE,
  FEATURE_FLAGS.TALKVIA_CORBAN_MANUAL_SIMULATORS,
  FEATURE_FLAGS.TALKVIA_CORBAN_PROPOSAL_PIPELINE,
];
const hasCorban = computed(() =>
  corbanFeatures.some(feature =>
    isFeatureEnabledonAccount.value(accountId.value, feature)
  )
);

const marketplaceSections = computed(() => [
  {
    key: 'services',
    title: 'Serviços',
    description:
      'Créditos para consultas, higienização e recursos de atendimento sob demanda.',
    icon: 'i-lucide-briefcase-business',
  },
  {
    key: 'addons',
    title: 'Adicionais do sistema',
    description:
      'Amplie usuários, caixas de entrada, o Talkvia Food e o suporte especializado da assinatura.',
    icon: 'i-lucide-blocks',
  },
  ...(hasCorban.value
    ? [
        {
          key: 'corban',
          title: 'Serviços Corban',
          description:
            'Conheça os planos de consultas do Benefício 360 e os produtos de campanhas.',
          icon: 'i-lucide-landmark',
        },
      ]
    : []),
]);

const campaignCatalog = computed(
  () => storePayload.value.campaign_catalog || []
);
const corbanProduct = ref(
  route.query.product === 'campaigns' ? 'campaigns' : 'individual'
);
const catalog = computed(() => storePayload.value.catalog || {});
const subscription = computed(() => storePayload.value.subscription || {});
const openBankSlips = computed(() => storePayload.value.open_bank_slips || []);
const pendingPurchases = computed(
  () => storePayload.value.pending_purchases || []
);
const guidance = computed(() => storePayload.value.guidance || []);
const creditProducts = computed(() => storePayload.value.credit_products || []);
const hasOpenBankSlips = computed(() => openBankSlips.value.length > 0);
const hasPendingPurchase = computed(() => pendingPurchases.value.length > 0);
const billingWarning = computed(() => storePayload.value.billing_warning || '');
const referralDiscount = computed(() =>
  Number(storePayload.value.referral_discount_percentage || 0)
);
const isBillingReady = computed(
  () => storePayload.value.billing_ready !== false
);

const productFeatures = (key, item) => {
  const featuresByKey = {
    additional_user: [
      'Novo acesso para atendimento',
      'Permissões e equipes configuráveis',
      'Cobrança recorrente mensal',
    ],
    additional_inbox: [
      'Canal oficial ou atendimento extra',
      'Organização por caixa de entrada',
      'Cobrança recorrente mensal',
    ],
    talkvia_food_essential: [
      'Cardápio, loja online e pedidos sem comissão',
      'Cozinha, balcão, mesa e entregas',
      '200 cadastros de clientes por WhatsApp/mês',
    ],
    talkvia_food_complete: [
      'Tudo do Food Essencial',
      'iFood, impressão térmica e clube de assinatura',
      'Análise de vendas pela Lia',
    ],
    consulting: [
      'Revisão de fluxos e integrações',
      'Acompanhamento operacional',
      'Pacote mensal especializado',
    ],
  };

  return (
    featuresByKey[key] || [
      item.description,
      item.unit_label,
      'Cobrança recorrente mensal',
    ]
  ).filter(Boolean);
};

const catalogItems = computed(() =>
  Object.entries(catalog.value).map(([key, item]) => ({
    key,
    featured: false,
    features: productFeatures(key, item),
    ...item,
  }))
);

const isBenefit360Item = item =>
  Boolean(
    item.metadata?.benefit360_plan_code ||
      Number(item.metadata?.benefit360_additional_monthly_limit || 0) > 0
  );
const systemAddonKeys = [
  'additional_user',
  'additional_inbox',
  'talkvia_food_essential',
  'talkvia_food_complete',
  'consulting',
];
const displayedCatalogItems = computed(() =>
  catalogItems.value.filter(item =>
    activeMarketplaceSection.value === 'corban'
      ? isBenefit360Item(item)
      : systemAddonKeys.includes(item.key)
  )
);
const catalogTitle = computed(() =>
  activeMarketplaceSection.value === 'corban'
    ? 'Benefício 360 — consultas individuais'
    : copy.sections.catalog
);
const catalogDescription = computed(() =>
  activeMarketplaceSection.value === 'corban'
    ? 'Escolha um plano ou amplie o teto mensal com pacotes adicionais.'
    : copy.sections.catalogDescription
);

const selectedItems = computed(() =>
  catalogItems.value
    .map(item => ({
      ...item,
      quantity: Number(quantities[item.key] || 0),
      total: Number(item.unit_price || 0) * Number(quantities[item.key] || 0),
    }))
    .filter(item => item.quantity > 0)
);

const monthlyTotal = computed(() =>
  selectedItems.value.reduce((sum, item) => sum + item.total, 0)
);

const currentSubscriptionValue = computed(() =>
  Number(subscription.value.value || 0)
);
const estimatedSubscriptionValue = computed(
  () => currentSubscriptionValue.value + monthlyTotal.value
);

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));
const creditUnitsFor = product => {
  const value = creditUnits[product.id];
  return Math.max(
    0,
    Number(value === undefined ? product.minimum_units || 1 : value || 0)
  );
};
const creditTotalFor = product => volumeTotal(product, creditUnitsFor(product));
const creditVolumeDiscountFor = product =>
  volumeDiscountFor(product, creditUnitsFor(product));
const formatPercent = value =>
  `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const creditAmountFor = product =>
  creditUnitsFor(product) * Number(product.credits_per_unit || 0);
const minimumCreditsFor = product =>
  Number(product.minimum_units || 1) * Number(product.credits_per_unit || 0);
const isBelowCreditMinimum = product =>
  creditUnitsFor(product) < Number(product.minimum_units || 1);

const creditProductPresentations = {
  confirme_online_cpf: {
    description:
      'Consulte nome, data de nascimento e nome da mãe por CPF. Créditos adicionais separados da franquia mensal gratuita, com os mesmos pacotes e preços do Talkvia Consulta Plus.',
    exampleTitle: 'Consulta Talkvia Cadastro',
    exampleDescription:
      'Informe um CPF e confira os dados cadastrais retornados. Um crédito por consulta.',
    type: 'confirme',
  },
  lemit_cpf: {
    description:
      'Encontre e valide dados de um CPF, incluindo informações cadastrais, telefones, WhatsApp e endereços.',
    exampleTitle: 'Exemplo de consulta de Higienização',
    exampleDescription:
      'Você informa um CPF válido e recebe os dados localizados de forma organizada.',
    type: 'hygiene',
  },
  nova_era_cpf: {
    description:
      'Localize dados cadastrais, perfil e celulares com WhatsApp a partir de um CPF.',
    exampleTitle: 'Exemplo de consulta Talkvia Contatos',
    exampleDescription:
      'Você informa um CPF válido e recebe os dados cadastrais e contatos localizados.',
    type: 'hygiene',
  },
  talkvia_contract: {
    tag: 'Assinatura simples e reforçada',
    description:
      'Contratos além da franquia mensal do seu plano. Um crédito por contrato gerado; o saldo não expira na virada do mês. Não inclui a biometria.',
    exampleTitle: 'Como os contratos avulsos são usados',
    exampleDescription:
      'Cada contrato gerado usa primeiro a franquia do plano no mês. Quando ela acaba, um contrato avulso é descontado do saldo.',
    type: 'contracts',
  },
  serpro_datavalid_facial: {
    tag: 'Biometria',
    description:
      'Assinatura biométrica de contratos: o Serpro confere nome, nascimento, situação do CPF e rosto do signatário nas bases oficiais do governo, com prova de vida. Um crédito por validação.',
    exampleTitle: 'Exemplo de assinatura biométrica',
    exampleDescription:
      'O cliente confirma o código do WhatsApp, informa os dados e tira uma foto ao vivo. O resultado fica no comprovante do contrato.',
    type: 'biometric',
  },
  captain_ai: {
    description:
      'Saldo pré-pago para a Lia responder clientes com IA usando a estrutura global da OpenAI.',
    exampleTitle: 'Exemplo de resposta da IA',
    exampleDescription:
      'A Lia interpreta a mensagem, consulta o contexto da conversa e prepara uma resposta natural.',
    type: 'ai',
  },
};

// Ícone e tom de cada crédito: o card abre pelo símbolo, não por um bloco de texto.
const creditProductIcons = {
  confirme_online_cpf: 'i-lucide-badge-check',
  lemit_cpf: 'i-lucide-user-search',
  nova_era_cpf: 'i-lucide-contact-round',
  captain_ai: 'i-woot-captain',
  serpro_datavalid_facial: 'i-hugeicons-finger-print-scan',
  talkvia_contract: 'i-hugeicons-contracts',
  sms: 'i-lucide-message-square-text',
};

const presentationFor = product => creditProductPresentations[product.code];
const creditIconFor = product =>
  creditProductIcons[product.code] || 'i-lucide-coins';
const productDescription = product =>
  presentationFor(product)?.description || product.description;
const openCreditExample = product => {
  selectedCreditExample.value = presentationFor(product);
  creditExampleDialogRef.value?.open();
};

const loadStore = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaStoreAPI.get();
    storePayload.value = data;
    if (activeMarketplaceSection.value === 'corban' && !hasCorban.value) {
      activeMarketplaceSection.value = 'addons';
    }
    (data.credit_products || []).forEach(product => {
      if (creditUnits[product.id] === undefined) {
        creditUnits[product.id] = Number(product.minimum_units || 1);
      }
    });
  } catch {
    useAlert(copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const addItem = key => {
  const item = catalog.value[key] || {};
  const maximum = Number(item.metadata?.maximum_quantity || 999);
  if (item.metadata?.benefit360_plan_code) {
    catalogItems.value.forEach(candidate => {
      if (candidate.metadata?.benefit360_plan_code)
        quantities[candidate.key] = 0;
    });
  }
  // Somente um plano do Talkvia Food por compra.
  if (item.metadata?.talkvia_food_plan) {
    catalogItems.value.forEach(candidate => {
      if (candidate.metadata?.talkvia_food_plan) quantities[candidate.key] = 0;
    });
  }
  quantities[key] = Math.min(maximum, Number(quantities[key] || 0) + 1);
};

const removeItem = key => {
  quantities[key] = Math.max(0, Number(quantities[key] || 0) - 1);
};

const openConfirmDialog = () => {
  if (!isBillingReady.value) {
    useAlert(billingWarning.value || copy.messages.billingNotReady);
    return;
  }
  if (hasOpenBankSlips.value) {
    useAlert(copy.messages.blocked);
    return;
  }
  if (hasPendingPurchase.value) {
    useAlert(copy.messages.pendingPurchase);
    return;
  }
  confirmDialogRef.value?.open();
};

const confirmPurchase = async () => {
  isSaving.value = true;
  try {
    const items = selectedItems.value.map(item => ({
      key: item.key,
      quantity: item.quantity,
    }));
    const { data } = await TalkviaStoreAPI.purchase(items);
    storePayload.value = data;
    Object.keys(quantities).forEach(key => {
      quantities[key] = 0;
    });
    confirmDialogRef.value?.close();
    useAlert(copy.messages.success);
  } catch (error) {
    useAlert(error.response?.data?.error || copy.messages.failed);
  } finally {
    isSaving.value = false;
  }
};

const buyCredits = async product => {
  buyingCreditProductId.value = product.id;
  try {
    const units = Number(creditUnits[product.id] || product.minimum_units || 1);
    const { data } = await TalkviaStoreAPI.purchaseCredits(product.id, units);
    if (data.status === 'paid') {
      // Ambiente de testes: a compra já nasce confirmada, sem boleto para abrir.
      useAlert('Compra confirmada. Os créditos já estão disponíveis.', {
        variant: 'positive',
      });
    } else {
      window.open(data.bank_slip_url || data.invoice_url, '_blank');
      useAlert(
        'Boleto gerado. Os créditos serão liberados após a confirmação do pagamento.',
        { variant: 'positive' }
      );
    }
    await loadStore();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível gerar o boleto.');
  } finally {
    buyingCreditProductId.value = null;
  }
};

onMounted(loadStore);
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto">
    <div class="mx-auto flex w-full flex-col gap-6 px-6 py-8 pb-24">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p
            class="mb-1 text-xs font-semibold uppercase tracking-wide text-n-brand"
          >
            {{ copy.labels.brand }}
          </p>
          <h1 class="mb-2 text-3xl font-semibold text-n-slate-12">
            {{ copy.title }}
          </h1>
          <p class="mb-0 max-w-3xl text-base text-n-slate-11">
            {{ copy.subtitle }}
          </p>
        </div>
        <button class="btn btn-secondary" @click="loadStore">
          {{ copy.actions.reload }}
        </button>
      </header>

      <div
        v-if="isLoading"
        class="rounded-lg border border-n-weak bg-n-solid-1 p-5"
      >
        {{ copy.loading }}
      </div>

      <section v-else class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <nav
          class="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:col-span-2"
          aria-label="Categorias do Marketplace"
        >
          <button
            v-for="section in marketplaceSections"
            :key="section.key"
            type="button"
            class="flex min-h-32 cursor-pointer items-start gap-4 rounded-2xl border border-solid p-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            :class="
              activeMarketplaceSection === section.key
                ? 'border-n-brand bg-n-brand text-white hover:brightness-95'
                : 'border-n-brand/40 bg-n-solid-1 text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10'
            "
            :aria-pressed="activeMarketplaceSection === section.key"
            @click="activeMarketplaceSection = section.key"
          >
            <span
              class="grid size-12 shrink-0 place-items-center rounded-xl"
              :class="
                activeMarketplaceSection === section.key
                  ? 'bg-white/20 text-white'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              <i :class="section.icon" class="size-6" />
            </span>
            <span>
              <strong class="block text-lg">
                {{ section.title }}
              </strong>
              <span
                class="mt-1 block text-sm leading-6"
                :class="
                  activeMarketplaceSection === section.key
                    ? 'text-white'
                    : 'text-n-slate-11'
                "
              >
                {{ section.description }}
              </span>
            </span>
          </button>
        </nav>

        <div
          v-if="referralDiscount > 0"
          class="xl:col-span-2 flex items-center gap-3 rounded-xl border border-n-teal-6 bg-n-teal-2 p-4 text-n-teal-12"
        >
          <i class="i-lucide-badge-percent size-6 shrink-0" />
          <div>
            <strong>Benefício de indicação aplicado</strong>
            <p class="mb-0 text-sm">
              Seu nível garante {{ referralDiscount }}% de desconto nos preços
              abaixo.
            </p>
          </div>
        </div>
        <section
          v-if="
            activeMarketplaceSection === 'services' && creditProducts.length
          "
          class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-5"
        >
          <h2 class="mb-1 text-xl font-semibold text-n-slate-12">
            Créditos de consultas
          </h2>
          <p class="mb-5 text-sm text-n-slate-11">
            Compra segura por boleto, com liberação automática após a
            confirmação do pagamento.
          </p>
          <div
            class="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr))]"
          >
            <article
              v-for="product in creditProducts"
              :key="product.id"
              class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-5 transition-colors hover:border-n-slate-6"
            >
              <header class="flex items-start gap-3">
                <span
                  class="grid size-11 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <Icon :icon="creditIconFor(product)" class="size-5" />
                </span>
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <strong class="text-base leading-6 text-n-slate-12">{{
                      product.name
                    }}</strong>
                    <span
                      class="shrink-0 rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs font-semibold text-n-slate-11"
                    >
                      {{ product.balance }} em saldo
                    </span>
                  </div>
                  <span
                    v-if="presentationFor(product)?.tag"
                    class="mt-1 inline-flex rounded-full bg-n-brand/10 px-2 py-0.5 text-xs font-semibold text-n-brand"
                  >
                    {{ presentationFor(product).tag }}
                  </span>
                  <p
                    class="mb-0 mt-1 line-clamp-3 min-h-[60px] text-sm leading-5 text-n-slate-11"
                    :title="productDescription(product)"
                  >
                    {{ productDescription(product) }}
                  </p>
                </div>
              </header>

              <div class="mt-4 flex items-end justify-between gap-3">
                <div class="min-w-0">
                  <span class="text-xl font-semibold text-n-slate-12">{{
                    formatMoney(product.unit_price)
                  }}</span>
                  <span
                    v-if="product.original_unit_price > product.unit_price"
                    class="ml-2 text-sm text-n-slate-10 line-through"
                    >{{ formatMoney(product.original_unit_price) }}</span
                  >
                  <p class="mb-0 text-xs text-n-slate-11">
                    por pacote de {{ product.credits_per_unit }} crédito<span
                      v-if="product.credits_per_unit !== 1"
                      >s</span
                    >
                  </p>
                </div>
                <button
                  v-if="presentationFor(product)"
                  type="button"
                  class="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-n-brand hover:underline"
                  @click="openCreditExample(product)"
                >
                  <i class="i-lucide-eye size-4" />
                  Ver exemplo
                </button>
              </div>

              <ul
                v-if="volumeTiersFor(product).length"
                class="mb-0 mt-3 flex list-none flex-wrap gap-1.5 p-0"
                aria-label="Desconto progressivo por quantidade"
              >
                <li
                  v-for="tier in volumeTiersFor(product)"
                  :key="tier.minUnits"
                  class="rounded-full border px-2.5 py-1 text-xs font-medium"
                  :class="
                    creditVolumeDiscountFor(product) === tier.discount
                      ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11'
                  "
                >
                  {{ tier.minUnits.toLocaleString('pt-BR') }}+ pacotes: −{{
                    formatPercent(tier.discount)
                  }}
                </li>
              </ul>

              <Input
                v-model="creditUnits[product.id]"
                type="number"
                label="Quantidade de pacotes"
                :min="String(product.minimum_units)"
                :placeholder="String(product.minimum_units)"
                class="mt-4"
              />

              <div
                v-if="isBelowCreditMinimum(product)"
                class="mt-3 flex items-start gap-2 rounded-lg bg-n-amber-2 px-3 py-2.5 text-sm text-n-amber-11"
              >
                <i class="i-lucide-info mt-0.5 size-4 shrink-0" />
                <span>
                  Compra mínima de
                  <strong>
                    {{ minimumCreditsFor(product).toLocaleString('pt-BR') }}
                    créditos
                  </strong>
                  ({{ product.minimum_units }} pacote<span
                    v-if="Number(product.minimum_units) !== 1"
                    >s</span
                  >).
                </span>
              </div>

              <dl class="mt-4 grid gap-2 rounded-lg bg-n-alpha-1 p-3 text-sm">
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-n-slate-11">Créditos da compra</dt>
                  <dd class="font-semibold text-n-slate-12">
                    {{ creditAmountFor(product).toLocaleString('pt-BR') }}
                  </dd>
                </div>
                <div
                  v-if="creditVolumeDiscountFor(product)"
                  class="flex items-center justify-between gap-3"
                >
                  <dt class="text-n-slate-11">Desconto por quantidade</dt>
                  <dd class="font-semibold text-n-teal-11">
                    −{{ formatPercent(creditVolumeDiscountFor(product)) }}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-3">
                  <dt class="text-n-slate-11">Valor estimado</dt>
                  <dd class="text-lg font-semibold text-n-brand">
                    {{ formatMoney(creditTotalFor(product)) }}
                  </dd>
                </div>
              </dl>

              <div class="mt-auto pt-4">
                <small
                  class="mb-2 flex min-h-5 items-center gap-1.5 text-n-teal-11"
                  :class="{ invisible: !product.special }"
                >
                  <i class="i-lucide-tag size-3.5" />
                  Condição especial aplicada
                </small>
                <Button
                  label="Comprar"
                  icon="i-lucide-shopping-cart"
                  :color="isBelowCreditMinimum(product) ? 'ruby' : 'blue'"
                  class="w-full"
                  :is-loading="buyingCreditProductId === product.id"
                  :disabled="
                    buyingCreditProductId !== null ||
                    isBelowCreditMinimum(product)
                  "
                  @click="buyCredits(product)"
                />
              </div>
            </article>
          </div>
        </section>
        <section
          v-else-if="activeMarketplaceSection === 'services'"
          class="rounded-xl border border-dashed border-n-weak bg-n-solid-1 px-6 py-14 text-center"
        >
          <i
            class="i-lucide-briefcase-business mx-auto mb-3 size-8 text-n-slate-9"
          />
          <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
            Nenhum serviço disponível
          </h2>
          <p class="mb-0 text-sm text-n-slate-11">
            Novos serviços aparecerão aqui quando estiverem disponíveis para sua
            conta.
          </p>
        </section>
        <div
          v-if="['addons', 'corban'].includes(activeMarketplaceSection)"
          class="grid min-w-0 gap-6"
        >
          <nav
            v-if="activeMarketplaceSection === 'corban'"
            aria-label="Produtos Corban"
            class="grid gap-3 sm:grid-cols-2"
          >
            <button
              v-for="option in [
                {
                  id: 'individual',
                  icon: 'i-hugeicons-user-search-01',
                  title: 'Consultas do Benefício 360',
                  description: 'Consulte uma pessoa pelo CPF ou matrícula.',
                },
                {
                  id: 'campaigns',
                  icon: 'i-hugeicons-user-group',
                  title: 'Campanhas de leads',
                  description: 'Planos mensais e pacotes avulsos de leads.',
                },
              ]"
              :key="option.id"
              type="button"
              :aria-pressed="corbanProduct === option.id"
              class="flex cursor-pointer items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
              :class="
                corbanProduct === option.id
                  ? 'border-n-brand bg-n-brand text-white'
                  : 'border-n-brand/30 bg-n-brand/5 text-n-slate-12 hover:border-n-brand hover:bg-n-brand/10'
              "
              @click="corbanProduct = option.id"
            >
              <span
                class="flex size-11 shrink-0 items-center justify-center rounded-lg"
                :class="
                  corbanProduct === option.id
                    ? 'bg-white/20'
                    : 'bg-n-brand/10 text-n-brand'
                "
                aria-hidden="true"
              >
                <i :class="option.icon" class="size-6" />
              </span>
              <span>
                <strong class="block text-base">{{ option.title }}</strong>
                <span class="mt-1 block text-sm">{{ option.description }}</span>
              </span>
            </button>
          </nav>
          <section
            v-if="
              activeMarketplaceSection !== 'corban' ||
              corbanProduct === 'individual'
            "
            class="grid gap-5"
          >
            <div class="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 class="mb-1 text-xl font-semibold text-n-slate-12">
                  {{ catalogTitle }}
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ catalogDescription }}
                </p>
              </div>
            </div>
            <div
              class="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,290px),1fr))]"
            >
              <article
                v-for="item in displayedCatalogItems"
                :key="item.key"
                class="store-plan"
                :class="{ 'store-plan--featured': item.featured }"
              >
                <div class="store-plan__top">
                  <span v-if="item.featured" class="store-plan__badge">
                    {{ copy.labels.recommended }}
                  </span>
                  <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
                    {{ item.name }}
                  </h3>
                  <p class="mb-0 min-h-12 text-sm leading-6 text-n-slate-11">
                    {{ item.description }}
                  </p>
                  <div class="mt-6">
                    <p
                      class="mb-0 flex items-end gap-1 text-3xl font-semibold text-n-slate-12"
                    >
                      {{ formatMoney(item.unit_price) }}
                      <span class="pb-1 text-sm font-normal text-n-slate-10">
                        {{ copy.labels.byMonth }}
                      </span>
                    </p>
                    <p
                      v-if="item.original_unit_price > item.unit_price"
                      class="mb-0 mt-1 text-sm text-n-slate-10 line-through"
                    >
                      {{ formatMoney(item.original_unit_price) }}
                    </p>
                    <p class="mb-0 mt-1 text-sm text-n-slate-10">
                      {{ item.unit_label }}
                    </p>
                  </div>
                </div>

                <ul class="store-plan__features">
                  <li v-if="item.metadata?.included_campaign_leads">
                    <span class="store-plan__check"
                      ><i class="i-hugeicons-checkmark-circle-02 size-4"
                    /></span>
                    <span
                      >Inclui Campanhas Essencial:
                      {{
                        Number(
                          item.metadata.included_campaign_leads
                        ).toLocaleString('pt-BR')
                      }}
                      leads/mês</span
                    >
                  </li>
                  <li v-for="feature in item.features" :key="feature">
                    <span class="store-plan__check">
                      <i class="i-lucide-check size-4" />
                    </span>
                    <span>{{ feature }}</span>
                  </li>
                </ul>

                <div class="mt-6 grid gap-3">
                  <div class="store-plan__quantity">
                    <button
                      class="stepper"
                      :aria-label="copy.actions.remove"
                      @click="removeItem(item.key)"
                    >
                      <i class="i-lucide-minus size-4" />
                    </button>
                    <span
                      class="min-w-8 text-center font-semibold text-n-slate-12"
                    >
                      {{ quantities[item.key] || 0 }}
                    </span>
                    <button
                      class="stepper"
                      :aria-label="copy.actions.add"
                      @click="addItem(item.key)"
                    >
                      <i class="i-lucide-plus size-4" />
                    </button>
                  </div>
                  <button
                    class="btn btn-primary w-full"
                    @click="addItem(item.key)"
                  >
                    <i class="i-lucide-shopping-cart size-4" />
                    {{ copy.actions.add }}
                  </button>
                </div>
              </article>
            </div>
          </section>
          <section
            v-if="
              activeMarketplaceSection === 'corban' &&
              corbanProduct === 'campaigns'
            "
            class="grid gap-6"
          >
            <div>
              <h2 class="text-xl font-semibold text-n-slate-12">
                Campanhas de leads
              </h2>
              <p class="text-sm text-n-slate-11">
                Todos os planos de Consulta Benefício incluem Campanhas
                Essencial: 5.000 leads por mês, sem cobrança adicional. Cada
                lead gerado consome um crédito de campanha, separado das
                consultas.
              </p>
              <p
                class="rounded-xl border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11"
              >
                O Essencial acompanha sua assinatura de consultas, respeitando
                os limites personalizados da empresa. Os preços abaixo são de
                contratação separada; essas compras ainda não estão disponíveis.
              </p>
            </div>
            <section
              v-for="group in [
                { kind: 'monthly', title: 'Planos mensais de campanhas' },
                { kind: 'one_off', title: 'Pacotes avulsos de leads' },
              ]"
              :key="group.kind"
              class="grid gap-4"
            >
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ group.title }}
              </h3>
              <div
                class="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,290px),1fr))]"
              >
                <article
                  v-for="item in campaignCatalog.filter(
                    product => product.kind === group.kind
                  )"
                  :key="item.key"
                  class="store-plan"
                >
                  <h4 class="text-xl font-semibold text-n-slate-12">
                    {{ item.name }}
                  </h4>
                  <p class="text-sm text-n-slate-11">{{ item.description }}</p>
                  <strong class="text-2xl text-n-slate-12"
                    >{{ Number(item.leads).toLocaleString('pt-BR') }} leads{{
                      group.kind === 'monthly' ? '/mês' : ''
                    }}</strong
                  >
                  <p
                    v-if="item.included_in_benefit_plan"
                    class="text-sm font-medium text-n-teal-11"
                  >
                    Incluído nos planos de Consulta Benefício
                  </p>
                  <p
                    v-if="item.price !== null"
                    class="text-lg font-semibold text-n-slate-12"
                  >
                    {{ formatMoney(item.price)
                    }}{{ group.kind === 'monthly' ? '/mês' : ' por pacote' }}
                    <span
                      v-if="item.included_in_benefit_plan"
                      class="block text-xs font-normal text-n-slate-11"
                      >Referência para contratação separada</span
                    >
                  </p>
                  <span
                    v-else-if="!item.included_in_benefit_plan"
                    class="text-sm text-n-slate-11"
                    >Preço em definição</span
                  >
                  <Button
                    :label="
                      item.included_in_benefit_plan
                        ? 'Incluído no plano de consulta'
                        : item.price !== null
                          ? 'Compra separada indisponível'
                          : 'Preço em definição'
                    "
                    disabled
                    class="mt-auto w-full"
                  />
                </article>
              </div>
            </section>
          </section>
        </div>

        <aside
          class="grid content-start gap-6"
          aria-label="Resumo e informações da compra"
        >
          <section
            v-if="billingWarning"
            class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-4 text-n-amber-12"
          >
            <h2 class="mb-2 text-base font-semibold">
              {{ copy.billing.notReady }}
            </h2>
            <p class="mb-0 text-sm">{{ billingWarning }}</p>
          </section>

          <section
            v-if="hasOpenBankSlips || hasPendingPurchase"
            class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4 text-n-ruby-12"
          >
            <h2 class="mb-2 text-base font-semibold">
              {{ copy.fields.openBills }}
            </h2>
            <p class="mb-3 text-sm">
              {{
                hasPendingPurchase
                  ? copy.messages.pendingPurchase
                  : copy.messages.blocked
              }}
            </p>
            <div v-if="hasOpenBankSlips" class="grid gap-2">
              <a
                v-for="payment in openBankSlips"
                :key="payment.id"
                :href="payment.invoiceUrl || payment.paymentLink"
                target="_blank"
                rel="noopener noreferrer"
                class="rounded-md bg-n-ruby-3 px-3 py-2 text-sm font-medium text-n-ruby-12"
              >
                {{ payment.description || payment.id }}
                {{ copy.labels.dash }}
                {{ formatMoney(payment.value) }}
              </a>
            </div>
          </section>

          <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
            <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
              {{ copy.sections.cart }}
            </h2>
            <p
              v-if="activeMarketplaceSection === 'services'"
              class="text-sm text-n-slate-11"
            >
              Os créditos avulsos são comprados pelo botão Comprar de cada
              serviço. Confira a quantidade e o valor no cartão antes de
              continuar. Este resumo reúne os planos e adicionais mensais
              selecionados.
            </p>
            <p
              v-if="
                activeMarketplaceSection === 'corban' &&
                corbanProduct === 'campaigns'
              "
              class="text-sm text-n-slate-11"
            >
              O Essencial acompanha os planos de consulta. Compras separadas de
              campanhas ainda não estão disponíveis.
            </p>
            <div v-if="!selectedItems.length" class="text-sm text-n-slate-11">
              {{ copy.empty.cart }}
            </div>
            <div v-else class="grid gap-3">
              <article
                v-for="item in selectedItems"
                :key="item.key"
                class="rounded-lg bg-n-alpha-2 p-3"
              >
                <div class="flex justify-between gap-3">
                  <div>
                    <p class="mb-1 font-medium text-n-slate-12">
                      {{ item.quantity }}{{ copy.labels.times }} {{ item.name }}
                    </p>
                    <p class="mb-0 text-sm text-n-slate-10">
                      {{ formatMoney(item.unit_price) }}
                      {{ copy.labels.byMonth }}
                    </p>
                  </div>
                  <strong class="text-n-slate-12">
                    {{ formatMoney(item.total) }}
                  </strong>
                </div>
              </article>
            </div>

            <div class="mt-5 grid gap-3 border-t border-n-weak pt-4">
              <div class="flex justify-between text-sm text-n-slate-11">
                <span>{{ copy.fields.currentSubscription }}</span>
                <strong class="text-n-slate-12">
                  {{ formatMoney(currentSubscriptionValue) }}
                </strong>
              </div>
              <div class="flex justify-between text-sm text-n-slate-11">
                <span>{{ copy.fields.monthlyIncrease }}</span>
                <strong class="text-n-slate-12">
                  {{ formatMoney(monthlyTotal) }}
                </strong>
              </div>
              <div class="flex justify-between text-base text-n-slate-12">
                <span>{{ copy.fields.newEstimatedValue }}</span>
                <strong>{{ formatMoney(estimatedSubscriptionValue) }}</strong>
              </div>
            </div>

            <button
              class="btn btn-primary mt-5 w-full"
              :disabled="
                !selectedItems.length ||
                hasOpenBankSlips ||
                hasPendingPurchase ||
                !isBillingReady
              "
              @click="openConfirmDialog"
            >
              {{ copy.actions.confirm }}
            </button>
          </section>

          <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
            <h2 class="mb-3 text-lg font-semibold text-n-slate-12">
              {{ copy.sections.guidance }}
            </h2>
            <ul class="m-0 grid list-none gap-3 p-0">
              <li
                v-for="item in guidance"
                :key="item"
                class="flex items-start gap-2 text-sm leading-5 text-n-slate-11"
              >
                <i
                  class="i-lucide-circle-check mt-0.5 size-4 shrink-0 text-n-teal-10"
                />
                <span>{{ item }}</span>
              </li>
            </ul>
          </section>
        </aside>
      </section>

      <Dialog ref="confirmDialogRef" width="2xl" @confirm="confirmPurchase">
        <template #default>
          <div class="grid gap-4">
            <h2 class="mb-0 text-xl font-semibold text-n-slate-12">
              {{ copy.actions.confirm }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ copy.sections.cart }}{{ copy.labels.colon }}
              {{ formatMoney(monthlyTotal) }} {{ copy.labels.recurring
              }}{{ copy.labels.periodMark }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ copy.fields.newEstimatedValue }}{{ copy.labels.colon }}
              {{ formatMoney(estimatedSubscriptionValue)
              }}{{ copy.labels.periodMark }}
            </p>
          </div>
        </template>
        <template #footer>
          <div class="flex w-full justify-end gap-3">
            <button
              class="btn btn-ghost"
              type="button"
              @click="confirmDialogRef.close()"
            >
              {{ copy.actions.cancel }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isSaving"
              @click="confirmPurchase"
            >
              {{ copy.actions.confirm }}
            </button>
          </div>
        </template>
      </Dialog>

      <Dialog
        ref="creditExampleDialogRef"
        width="2xl"
        :title="selectedCreditExample?.exampleTitle"
        :description="selectedCreditExample?.exampleDescription"
        :show-cancel-button="false"
        confirm-button-label="Entendi"
        @confirm="creditExampleDialogRef.close()"
      >
        <div
          v-if="selectedCreditExample?.type === 'confirme'"
          class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <p>Exemplo fictício de dados retornados</p>
          <div class="credit-example-field">
            <span>Nome</span><strong>Cliente de exemplo</strong>
          </div>
          <div class="credit-example-field">
            <span>Data de nascimento</span><strong>15/05/1980</strong>
          </div>
          <div class="credit-example-field">
            <span>Nome da mãe</span><strong>Nome de exemplo</strong>
          </div>
          <p class="text-sm text-n-slate-11">
            Este serviço não informa telefone no retorno cadastral.
          </p>
        </div>
        <div
          v-else-if="selectedCreditExample?.type === 'hygiene'"
          class="grid gap-4"
        >
          <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
            <p class="mb-3 text-xs font-semibold uppercase text-n-slate-10">
              Resultado ilustrativo
            </p>
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="credit-example-field">
                <span>Nome</span><strong>Cliente localizado</strong>
              </div>
              <div class="credit-example-field">
                <span>Situação do CPF</span><strong>Regular</strong>
              </div>
              <div class="credit-example-field">
                <span>Celular</span><strong>(11) 9****-1234</strong>
                <small class="text-n-teal-11">WhatsApp confirmado</small>
              </div>
              <div class="credit-example-field">
                <span>Localização</span><strong>São Paulo — SP</strong>
              </div>
            </div>
          </div>
          <p class="mb-0 text-xs leading-5 text-n-slate-10">
            Os campos encontrados variam conforme a disponibilidade das fontes
            consultadas. Os dados deste exemplo são fictícios.
          </p>
        </div>

        <div
          v-else-if="selectedCreditExample?.type === 'contracts'"
          class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <p>Exemplo fictício de um mês</p>
          <div class="credit-example-field">
            <span>Franquia do plano</span><strong>30 contratos</strong>
          </div>
          <div class="credit-example-field">
            <span>Contratos gerados no mês</span><strong>34</strong>
          </div>
          <div class="credit-example-field">
            <span>Contratos avulsos usados</span><strong>4</strong>
          </div>
          <p class="text-sm text-n-slate-11">
            Vale para assinatura simples e reforçada. A assinatura biométrica
            usa também os Créditos de biometria.
          </p>
        </div>
        <div
          v-else-if="selectedCreditExample?.type === 'biometric'"
          class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-4"
        >
          <p>Exemplo fictício do comprovante</p>
          <div class="credit-example-field">
            <span>Código de confirmação</span
            ><strong>WhatsApp final 1234</strong>
          </div>
          <div class="credit-example-field">
            <span>Nome e nascimento</span><strong>Conferem</strong>
          </div>
          <div class="credit-example-field">
            <span>Comparação facial</span><strong>Alta probabilidade</strong>
          </div>
          <div class="credit-example-field">
            <span>Prova de vida</span><strong>Aprovada</strong>
          </div>
          <p class="text-sm text-n-slate-11">
            A comparação usa a foto das bases oficiais; CPFs sem foto nessas
            bases não podem usar a assinatura biométrica.
          </p>
        </div>
        <div
          v-else-if="selectedCreditExample?.type === 'ai'"
          class="grid gap-3"
        >
          <div
            class="ml-auto max-w-[85%] rounded-xl bg-n-brand px-4 py-3 text-sm text-white"
          >
            Olá! Gostaria de saber quais documentos preciso enviar.
          </div>
          <div
            class="max-w-[90%] rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 text-sm leading-6 text-n-slate-12"
          >
            <div
              class="mb-2 flex items-center gap-2 font-semibold text-n-brand"
            >
              <Icon icon="i-woot-captain" class="size-4" />Lia
            </div>
            Claro! Vou orientar você. Para começar, envie um documento com foto
            e o comprovante solicitado no atendimento. Se preferir, também posso
            explicar cada etapa do processo.
          </div>
          <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
            Exemplo ilustrativo. O consumo de créditos varia conforme o modelo
            de IA utilizado e o processamento necessário para cada resposta.
          </p>
        </div>
      </Dialog>
    </div>
  </main>
</template>

<style scoped>
.btn {
  @apply inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60;
}

.btn-primary {
  @apply bg-n-brand text-white hover:opacity-90;
}

.btn-secondary {
  @apply border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.btn-ghost {
  @apply text-n-slate-11 hover:text-n-slate-12;
}

.stepper {
  @apply flex size-10 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2;
}

.store-plan {
  @apply relative flex min-h-[440px] flex-col justify-between overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand/40 hover:shadow-md;
}

.store-plan::before {
  @apply absolute inset-x-0 top-0 h-1 bg-n-alpha-3 content-[''];
}

.store-plan--featured {
  @apply border-n-brand bg-n-solid-2 shadow-[0_18px_48px_rgba(37,99,235,0.16)];
}

.store-plan--featured::before {
  @apply bg-n-brand;
}

.store-plan__top {
  @apply relative;
}

.store-plan__badge {
  @apply mb-4 inline-flex rounded-full bg-n-brand px-3 py-1 text-xs font-semibold text-white;
}

.store-plan__features {
  @apply mt-6 grid list-none gap-3 border-t border-n-weak p-0 pt-5;
}

.store-plan__features li {
  @apply flex items-start gap-3 text-sm leading-5 text-n-slate-11;
}

.store-plan__check {
  @apply mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-n-brand;
}

.store-plan__quantity {
  @apply flex items-center justify-center gap-3 rounded-lg bg-n-alpha-2 p-2;
}

.credit-example-field {
  @apply flex min-h-20 flex-col rounded-lg bg-n-alpha-2 p-3 text-sm;
}

.credit-example-field span {
  @apply mb-1 text-xs text-n-slate-10;
}

.credit-example-field strong {
  @apply text-n-slate-12;
}
</style>
